import {
  createRouter as createTanStackRouter,
  isRedirect,
} from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import * as Sentry from '@sentry/tanstackstart-react'

import { getContext } from './integrations/tanstack-query/root-provider'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN

if (import.meta.env.PROD && !sentryDsn) {
  throw new Error('[Sentry] VITE_SENTRY_DSN is required in production')
}

export function getRouter() {
  const router = createTanStackRouter({
    routeTree,

    context: getContext(),

    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
  })

  if (!router.isServer) {
    if (!sentryDsn) {
      return router
    }

    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      // Tunnel via notre propre domaine pour contourner les ad-blockers.
      // Voir src/routes/api/tunnel.ts pour le proxy vers Sentry.
      tunnel: '/api/tunnel',

      // Adds request headers and IP for users, for more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/tanstackstart-react/configuration/options/#sendDefaultPii
      sendDefaultPii: true,
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.replayIntegration(),
      ],
      // Enable logs to be sent to Sentry
      enableLogs: true,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for tracing.
      // We recommend adjusting this value in production.
      // Learn more at https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: 1.0,
      // Capture Replay for 0% of all sessions,
      // plus for 100% of sessions with an error.
      // Learn more at https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        if (isRedirect(hint.originalException)) return null
        return event
      },
    })
  }

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
